import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { formatTrafficFetchedAt } from "./mapUtils"
import { TrafficIncident } from "./routeTraffic"
import { incidentIconSvg } from "./TrafficIncidentsLayer"
import {
  IncidentOnRoute,
  incidentIconId,
  incidentTimes,
  incidentTypeLabel,
} from "./trafficIncidents"

type TrafficIncidentsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  incidents: IncidentOnRoute[]
  trafficFetchedAt?: string
  onSelect: (incident: TrafficIncident) => void
}

const iconSrc = (type?: string | null) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    incidentIconSvg(incidentIconId(type)),
  )}`

/** Every incident on the route in one list, nearest first. */
const TrafficIncidentsDialog = ({
  open,
  onOpenChange,
  incidents,
  trafficFetchedAt,
  onSelect,
}: TrafficIncidentsDialogProps) => {
  const { t } = useTranslation()
  const fetchedAt = formatTrafficFetchedAt(trafficFetchedAt)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("maps.routeTraffic.dialogTitle", { value: incidents.length })}
          </DialogTitle>
          <DialogDescription>
            {fetchedAt
              ? t("maps.routeTraffic.dialogDescription", { time: fetchedAt })
              : t("maps.routeTraffic.dialogDescriptionNoTime")}
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {incidents.map(({ incident, km }) => {
            return (
              <li key={incident.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-2 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => onSelect(incident)}
                >
                  <span className="w-16 shrink-0 text-right text-sm font-medium tabular-nums">
                    {Math.round(km)} km
                  </span>
                  <img
                    src={iconSrc(incident.type)}
                    alt=""
                    width={22}
                    height={22}
                    className="shrink-0"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">
                      {incidentTypeLabel(incident.type)}
                      {incident.description &&
                      incident.description !== incidentTypeLabel(incident.type)
                        ? ` · ${incident.description}`
                        : ""}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-300">
                      {[incident.criticality, ...incidentTimes(incident)]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </DialogContent>
    </Dialog>
  )
}

export default TrafficIncidentsDialog
